import { useMemo, useRef, useState } from "react";
import { MousePointer2, Type, Timer, Table2, ArrowUp } from "lucide-react";

const RING = {
  click: 'ring-2 ring-[hsl(var(--step-click))]',
  fill: 'ring-2 ring-[hsl(var(--step-fill))]',
  wait: 'ring-2 ring-[hsl(var(--step-wait))]',
  extract: 'ring-2 ring-[hsl(var(--step-extract))]',
};

const EMPTY_MODES = [
  { icon: MousePointer2, label: 'Clicar', desc: 'Botões e links', color: 'text-[hsl(var(--step-click))] bg-[hsl(var(--step-click))]/10' },
  { icon: Type, label: 'Preencher', desc: 'Inputs e datas', color: 'text-[hsl(var(--step-fill))] bg-[hsl(var(--step-fill))]/10' },
  { icon: Timer, label: 'Esperar', desc: 'Pausa entre acções', color: 'text-[hsl(var(--step-wait))] bg-[hsl(var(--step-wait))]/10' },
  { icon: Table2, label: 'Extrair', desc: 'A tabela final', color: 'text-[hsl(var(--step-extract))] bg-[hsl(var(--step-extract))]/10' },
];

export default function RecorderCanvas({ screenshotUrl, mode = 'click', steps = [], onCanvasClick, busy }) {
  const wrapRef = useRef(null);
  const imgRef = useRef(null);
  const [ripple, setRipple] = useState(null);

  const cursorClass = useMemo(() => {
    if (mode === 'click') return 'cursor-crosshair';
    if (mode === 'fill') return 'cursor-text';
    if (mode === 'extract') return 'cursor-cell';
    return 'cursor-wait';
  }, [mode]);

  function handleClick(e) {
    if (busy) return;
    const target = imgRef.current || wrapRef.current;
    if (!target) return;
    const rect = target.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    if (x < 0 || x > 1 || y < 0 || y > 1) return;
    setRipple({ x, y, id: Date.now() });
    window.setTimeout(() => setRipple(null), 220);
    onCanvasClick?.({ x: Math.max(0, Math.min(1, x)), y: Math.max(0, Math.min(1, y)), mode });
  }

  const markers = (steps || []).filter(s => s.x != null && s.y != null && s.type !== 'wait');

  return (
    <div ref={wrapRef} data-testid="recorder-canvas" onClick={handleClick}
         className={`relative overflow-hidden rounded-2xl border border-border bg-[hsl(var(--canvas))] shadow-[0_10px_30px_-18px_rgba(15,23,42,0.35)] dark:shadow-[0_10px_30px_-18px_rgba(0,0,0,0.65)] ${cursorClass}`}>
      <div className="pointer-events-none absolute inset-0 opacity-[0.35] canvas-grid" />
      {screenshotUrl ? (
        <img ref={imgRef} data-testid="recorder-canvas-screenshot" src={screenshotUrl} alt="Captura de ecrã do navegador em direto"
             className="relative z-10 block w-full h-auto select-none" draggable={false} />
      ) : (
        <div className="relative z-10 px-6 py-10 min-h-[440px] grid place-items-center" data-testid="recorder-canvas-empty">
          <div className="max-w-xl w-full text-center space-y-5">
            <div className="flex justify-center">
              <div className="relative">
                <div className="h-14 w-14 rounded-full bg-primary/10 grid place-items-center">
                  <ArrowUp className="h-6 w-6 text-primary animate-bounce"/>
                </div>
              </div>
            </div>
            <div>
              <div className="text-base font-semibold">Cola um URL e clica em <span className="text-primary">Iniciar sessão</span></div>
              <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
                Quando a sessão arrancar, vês aqui o screenshot ao vivo da página alvo.
                Clica nele para gravar passos.
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-2">
              {EMPTY_MODES.map((m, i) => (
                <div key={i} className="rounded-lg border border-border bg-card/70 px-2 py-2.5 flex flex-col items-center gap-1.5">
                  <div className={`h-8 w-8 rounded-md grid place-items-center ${m.color}`}>
                    <m.icon className="h-4 w-4"/>
                  </div>
                  <div className="text-[11px] font-semibold leading-none">{m.label}</div>
                  <div className="text-[10px] text-muted-foreground leading-tight">{m.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      <div className="absolute inset-0 z-20 pointer-events-none">
        {markers.map((s, idx) => (
          <div key={s.id || idx} style={{ left: `${(s.x / 1366) * 100}%`, top: `${(s.y / 900) * 100}%` }} className="absolute -translate-x-1/2 -translate-y-1/2">
            <div className={`h-6 w-6 rounded-full bg-card text-foreground border border-border shadow-sm grid place-items-center text-[11px] font-semibold ${RING[s.type] || ''}`}>
              {idx + 1}
            </div>
          </div>
        ))}
        {ripple && (
          <div className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-ring/60" style={{ left: `${ripple.x * 100}%`, top: `${ripple.y * 100}%`, width: 24, height: 24, animation: 'ripple 220ms cubic-bezier(0.2,0.8,0.2,1) forwards' }} />
        )}
      </div>
      {busy && (
        <div className="absolute inset-0 z-30 grid place-items-center bg-background/40 backdrop-blur-[2px]">
          <div className="text-sm text-muted-foreground bg-card border border-border rounded-lg shadow-sm px-3 py-2">Working…</div>
        </div>
      )}
    </div>
  );
}
